import { VendorQuote, VendorQuoteItem } from '../types/ui/VendorQuote';
import { apiId } from '../utils/apiId';
import { apiGet, apiPost, apiPut, apiDelete, planningPath } from './PlanningApiClient';

interface ApiVendorQuote {
  id: string;
  category_id: string;
  vendor_id: string | null;
  vendor_name: string;
  event_ids: string[];
  date_received: string | null;
  notes: string | null;
  status: 'pending' | 'accepted' | 'rejected';
  total_amount: number | string;
  contingency_amount?: number | string;
  created_at: string;
  updated_at: string;
}

function transform(row: ApiVendorQuote): VendorQuote {
  return {
    id: String(row.id),
    categoryId: String(row.category_id),
    vendorId: row.vendor_id || undefined,
    vendorName: row.vendor_name,
    eventIds: row.event_ids || [],
    // Postgres DATE columns come back as a full ISO datetime string
    // ("2026-07-01T00:00:00.000Z") — <input type="date"> only accepts a
    // bare YYYY-MM-DD and silently renders blank on anything else.
    dateReceived: row.date_received ? row.date_received.slice(0, 10) : undefined,
    notes: row.notes || undefined,
    status: row.status,
    totalAmount: Number(row.total_amount) || 0,
    contingencyAmount: Number(row.contingency_amount) || 0,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

interface ApiVendorQuoteItem {
  id: string;
  quote_id: string;
  description: string;
  quantity: number | string;
  unit_cost: number | string;
  sort_order: number;
  category_id: string | null;
  is_bundle_adjustment: boolean;
}

function transformItem(row: ApiVendorQuoteItem): VendorQuoteItem {
  return {
    id: String(row.id),
    quoteId: String(row.quote_id),
    description: row.description,
    quantity: Number(row.quantity),
    unitCost: Number(row.unit_cost),
    sortOrder: row.sort_order,
    categoryId: row.category_id != null ? String(row.category_id) : null,
    isBundleAdjustment: !!row.is_bundle_adjustment,
  };
}

export async function loadQuotes(): Promise<VendorQuote[]> {
  const response = await apiGet<{ quotes: ApiVendorQuote[] }>(planningPath('/quotes'));
  return (response.quotes || []).map(transform);
}

export async function createQuote(data: {
  categoryId: string;
  vendorId?: string;
  vendorName: string;
  eventIds?: string[];
  dateReceived?: string;
  notes?: string;
}): Promise<VendorQuote> {
  const row = await apiPost<ApiVendorQuote>(planningPath('/quotes'), {
    category_id: apiId(data.categoryId),
    vendor_id: data.vendorId || null,
    vendor_name: data.vendorName,
    event_ids: data.eventIds ?? [],
    date_received: data.dateReceived || null,
    notes: data.notes || null,
  });
  return transform(row);
}

export async function updateQuote(id: string, updates: Partial<Pick<VendorQuote, 'vendorId' | 'vendorName' | 'categoryId' | 'eventIds' | 'dateReceived' | 'notes' | 'contingencyAmount'>>): Promise<VendorQuote> {
  const body: Record<string, unknown> = {};
  if (updates.vendorId !== undefined) body.vendor_id = updates.vendorId || null;
  if (updates.vendorName !== undefined) body.vendor_name = updates.vendorName;
  if (updates.categoryId !== undefined) body.category_id = apiId(updates.categoryId);
  if (updates.eventIds !== undefined) body.event_ids = updates.eventIds;
  if (updates.dateReceived !== undefined) body.date_received = updates.dateReceived;
  if (updates.notes !== undefined) body.notes = updates.notes;
  if (updates.contingencyAmount !== undefined) body.contingency_amount = updates.contingencyAmount;
  const row = await apiPut<ApiVendorQuote>(planningPath(`/quotes/${id}`), body);
  return transform(row);
}

export async function deleteQuote(id: string): Promise<void> {
  await apiDelete(planningPath(`/quotes/${id}`));
}

// Accepting creates the real budget_items vendor slot server-side — the
// budget page will show it once refreshed.
export async function acceptQuote(id: string): Promise<{ quote: VendorQuote; budgetItemId: string }> {
  const result = await apiPost<{ quote: ApiVendorQuote; budget_item: { id: string } }>(planningPath(`/quotes/${id}/accept`), {});
  return { quote: transform(result.quote), budgetItemId: String(result.budget_item.id) };
}

export async function rejectQuote(id: string): Promise<VendorQuote> {
  const row = await apiPost<ApiVendorQuote>(planningPath(`/quotes/${id}/reject`), {});
  return transform(row);
}

// ── Vendor Quote Line Items ──

export async function loadQuoteItems(quoteId: string): Promise<VendorQuoteItem[]> {
  const response = await apiGet<{ items: ApiVendorQuoteItem[] }>(planningPath(`/quotes/${quoteId}/items`));
  return (response.items || []).map(transformItem);
}

// Throws on failure (including the 409 the API returns once the quote is
// no longer pending) — callers show that error rather than silently
// swallowing it, since a locked-out write is meaningful, not a background
// sync detail.
export async function addQuoteItem(
  quoteId: string, description: string, quantity: number, unitCost: number,
  options?: { categoryId?: string | null; isBundleAdjustment?: boolean }
): Promise<VendorQuoteItem> {
  const row = await apiPost<ApiVendorQuoteItem>(planningPath(`/quotes/${quoteId}/items`), {
    description, quantity, unit_cost: unitCost,
    category_id: apiId(options?.categoryId),
    is_bundle_adjustment: options?.isBundleAdjustment ?? false,
  });
  return transformItem(row);
}

export async function updateQuoteItem(quoteId: string, itemId: string, fields: Partial<Pick<VendorQuoteItem, 'description' | 'quantity' | 'unitCost' | 'categoryId' | 'isBundleAdjustment'>>): Promise<VendorQuoteItem> {
  const body: Record<string, unknown> = {};
  if (fields.description !== undefined) body.description = fields.description;
  if (fields.quantity !== undefined) body.quantity = fields.quantity;
  if (fields.unitCost !== undefined) body.unit_cost = fields.unitCost;
  if (fields.categoryId !== undefined) body.category_id = apiId(fields.categoryId);
  if (fields.isBundleAdjustment !== undefined) body.is_bundle_adjustment = fields.isBundleAdjustment;
  const row = await apiPut<ApiVendorQuoteItem>(planningPath(`/quotes/${quoteId}/items/${itemId}`), body);
  return transformItem(row);
}

export async function deleteQuoteItem(quoteId: string, itemId: string): Promise<void> {
  await apiDelete(planningPath(`/quotes/${quoteId}/items/${itemId}`));
}
